import LoginBrandBackground from "@funded-here-interface/common/src/assets/loginBrandBackground.webp";
import { FONT_FAMILY } from "@funded-here-interface/common/src/constant/typography";
import { MantineTheme } from "@mantine/core";
import { createStyles, keyframes } from "@mantine/emotion";

/**
 * Shared split layout for /login and /registration.
 *
 * The two screens are mirror images — Login is [brand | form], Registration is
 * [form | brand] — and each used to render its OWN copy of the brand panel.
 * Navigating between them therefore unmounted one whole tree and mounted the
 * other, so the illustration was destroyed and recreated rather than moved.
 *
 * This layout owns a single brand panel and slides it between the two sides.
 * The geometry is a three-slot track, each slot half the viewport wide:
 *
 *     track:  [ registration form ][ brand ][ login form ]
 *     login:                       └──── visible ───────┘   translateX(-1/3)
 *     reg:    └──── visible ──────┘                         translateX(0)
 *
 * Sliding the WHOLE track by exactly one slot moves the brand panel across and
 * carries both forms with it — one animated element, and the two forms travel
 * in lockstep with the panel for free. The off-screen slot is empty except
 * during a transition (see AuthSplitLayout.tsx), so the form that isn't on
 * screen is not mounted — which matters because Registration initialises a
 * Cloudflare Turnstile widget on mount.
 */

// Gentle breathing for the ambient corner glow — opacity only, so it has no hard
// center point (unlike a conic sweep, which shows a visible convergence seam).
const glow = keyframes({
  "0%, 100%": { opacity: 0.5 },
  "50%": { opacity: 1 },
});

// Plain fade for the whole layout — no transform, so a full-bleed page doesn't
// visibly zoom or shift on load.
const fadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

/** Shared with AuthSplitLayout.tsx's un-stage timer, so the two can't disagree. */
export const SLIDE_DURATION_MS = 520;

/**
 * The container width at which the split collapses to a single column: brand
 * panel gone, track full-width, no slide.
 *
 * One number shared by the JS check (useContainerCollapsed.ts, which explains
 * why it measures the container rather than the viewport) and every
 * `@container` rule here, in LoginCss.tsx and in Registration.styles.tsx.
 */
export const SPLIT_COLLAPSED_PX = 820;

const splitCollapsedQuery = (containerName: string) =>
  `@container ${containerName} (max-width: ${SPLIT_COLLAPSED_PX}px)`;

export const useStyles = createStyles((theme: MantineTheme) => ({
  // Full-page backdrop — plain white. Carries the container so the split
  // breakpoint below is measured against this element's width, matching the
  // `loginpage` container the two screens' own styles already query.
  parentContainer: {
    containerType: "inline-size",
    // BOTH names, space-separated: the two screens' own styles were written
    // against their own former wrappers and query different container names
    // (`loginpage` in LoginCss, `registrationpage` in Registration.styles).
    // Declaring one name here would silently stop the other screen's container
    // queries from ever matching.
    containerName: "loginpage registrationpage",
    display: "flex",
    width: "100%",
    // `100dvh`, not `100vh`: this white backdrop has to reach the bottom of the
    // screen, but `100vh` on mobile means the *largest* viewport — taller than
    // what is actually visible while the address bar is up — which produced a
    // phantom scroll. `dvh` tracks the real visible height.
    minHeight: "100dvh",
    boxSizing: "border-box",
    position: "relative",
    fontFamily: FONT_FAMILY,
    background: "#ffffff",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 16px",
    [`@media (max-width: ${theme.breakpoints.sm})`]: {
      padding: "8px",
    },
  },

  /**
   * Clips the track. Everything outside the two visible slots — the form that
   * is sliding away, and the one arriving — is hidden by this overflow rather
   * than by being unmounted, which is what lets them animate.
   *
   * Also the height authority: this run is fixed, and a form aligns ITSELF to
   * it rather than the illustration resizing to the form. Registration opts
   * into that alignment from its own styles (see `formPanel`).
   */
  viewport: {
    position: "relative",
    // `flex`, so `track` stretches to this element's definite height. As an
    // auto-height block, the column shrank to its content and clipped the
    // bottom of a form showing every validation error (see `track`, `formPanel`).
    display: "flex",
    // `clip`, not `hidden`: a `hidden` box can still be scrolled by focus (Tab,
    // autofocus, Turnstile) into the off-screen slot, leaving the track offset
    // for every later slide. `clip` can't be scrolled at all.
    overflow: "clip",
    width: "100%",
    minHeight: "clamp(600px, 92vh, 820px)",
    maxHeight: "calc(100vh - 16px)",
    boxSizing: "border-box",
    animation: `${fadeIn} 0.5s ease both`,
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none",
    },
    // Below the split breakpoint the brand panel is hidden and the lone form
    // sizes to its own content, so the fixed height would leave dead space.
    [splitCollapsedQuery("loginpage")]: {
      minHeight: "auto",
      maxHeight: "none",
      overflow: "visible",
      /**
       * Top-align once the split collapses. Set here rather than on
       * `parentContainer`, because a container query can't style the element
       * that declares the container.
       *
       * `stretch`, not `flex-start`: the column keeps its full height, so
       * `formPanel` can put the logo at the top and centre the form below it.
       * `flex-start` shrink-wraps the column and drags the form up with the logo.
       */
      alignSelf: "stretch",
    },
  },

  /**
   * 150% wide — three slots of 50% viewport each. `translateX` is expressed as
   * a fraction of the TRACK, so one slot is 1/3 (33.3333%), not 50%.
   */
  track: {
    display: "flex",
    // `flex: 0 0 150%`, not `width: 150%`: as `viewport`'s flex child, the
    // default `flex-shrink: 1` would squeeze it to 100% and put the
    // `--track-offset` slide on the wrong base. No `height` either: a
    // percentage height would block the stretch that gives it a definite one.
    flex: "0 0 150%",
    /**
     * The offset arrives as `--track-offset` from AuthSplitLayout.tsx, NOT as
     * an inline `transform`. An inline transform outranks every class rule, so
     * the mobile override below could never switch it off — the single-column
     * track stayed shifted a third of the viewport left and carried the whole
     * login/signup form off the left edge of the screen.
     *
     * Reading it through a variable puts the decision back in CSS: the
     * component supplies a value, this rule decides whether to use it.
     */
    transform: "var(--track-offset, translateX(0))",
    transition: `transform ${SLIDE_DURATION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
    // Honour the OS setting: the layout still swaps, it just cuts rather than
    // slides. A 500ms horizontal sweep across the full viewport is exactly the
    // kind of motion that triggers vestibular discomfort.
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
    },
    // Single-column below the breakpoint: no brand panel, so there is nothing
    // to slide past and no second slot to slide to. The track is just the one
    // mounted form at full width, sitting still.
    [splitCollapsedQuery("loginpage")]: {
      flex: "0 0 100%",
      transform: "none",
      transition: "none",
    },
  },

  /**
   * One third of the track = half the viewport. The gap between the two
   * visible halves is paid for by each slot's own inline padding (half a gap
   * per side, so adjacent slots add up to a full one) rather than a flex
   * `gap`, which would make the one-slot translate no longer line up.
   */
  slot: {
    flex: "0 0 33.3333%",
    maxWidth: "33.3333%",
    display: "flex",
    boxSizing: "border-box",
    paddingInline: "calc(clamp(40px, 5vw, 120px) / 2)",
    [splitCollapsedQuery("loginpage")]: {
      flex: "1 1 100%",
      maxWidth: "100%",
      // The single-column gutter. There is no neighbouring slot to split a gap
      // with down here, so the desktop formula above doesn't apply — but 0 left
      // the form running to the page edge with only `parentContainer`'s 8px
      // between the inputs and the screen. 16 here puts the total at 24px,
      // which is the gutter the form content was drawn against.
      // `box-sizing: border-box` keeps it inside the collapsed track's basis.
      paddingInline: 16,
    },
  },

  /**
   * A slot with nothing in it — the off-screen side outside a transition.
   * Kept in the flow at desktop widths because the track's geometry depends on
   * all three slots existing; removed below the breakpoint, where the track is
   * a single column and an empty third would squeeze the form.
   */
  slotEmpty: {
    [splitCollapsedQuery("loginpage")]: {
      display: "none",
    },
  },

  /**
   * Holds the routed form. Scrolls internally if a form outgrows the frame.
   *
   * `safe center` (see `justifyContent` below) is the default and is what
   * Login gets. A screen that wants its heading and footer pinned to the
   * illustration's top and bottom edges opts in from its OWN styles instead,
   * by giving its `inputGroupContainer` `flex: 1 1 auto` — a growing child
   * leaves no free space for `center`/`safe center` to distribute, so the
   * block fills the column. Registration does this; Login deliberately does
   * not.
   */
  formPanel: {
    flex: "1 1 100%",
    minWidth: 0,
    position: "relative",
    zIndex: 3,
    display: "flex",
    flexDirection: "column",
    // `safe center`: plain `center` splits an overflowing column's excess
    // between both ends, pushing Registration's heading above the scrollable
    // area; `safe` falls back to start-alignment when content overflows.
    // Emitted as two declarations, plain `center` first, for browsers that
    // don't know `safe` and drop that one. Reversed, `center` would win.
    justifyContent: ["center", "safe center"],
    alignItems: "center",
    overflowY: "auto",
    boxSizing: "border-box",
    /**
     * Top-aligned once the brand panel is gone.
     *
     * Centring is right beside the illustration, where the form is balancing
     * against a full-height panel. On its own in a single column there is
     * nothing to balance against, so the slack split above and below just
     * pushed the logo into the middle of the screen with a large dead band
     * over it. Anchoring to the top puts the logo where it belongs and lets
     * the leftover height fall below the form.
     *
     * Only Login is affected. Registration's `inputGroupContainer` is
     * `flex: 1 1 auto` (see its own styles), so its content already consumes
     * the column and there is no free space for `justify-content` to place.
     */
    [splitCollapsedQuery("loginpage")]: {
      justifyContent: "flex-start",
      // Gap above the logo. This is the single knob for how high the logo
      // sits — the form below it is centred by auto margins (see Login's
      // `heading`) and moves independently of this value.
      paddingBlockStart: 40,
    },
  },

  // --- Illustration ---------------------------------------------------------
  // The tree sits inside its own bordered/rounded frame rather than bleeding to
  // the page edge. Padding stays 0 so the illustration reads as large as the
  // frame allows — the logo floats over it via absolute positioning instead of
  // reserving space.
  brandSlot: {
    // The brand is the one slot that is never empty, and never hidden by
    // `slotEmpty` — it is hidden outright below the split breakpoint.
    [splitCollapsedQuery("loginpage")]: {
      display: "none",
    },
  },
  brandPanel: {
    flex: "1 1 100%",
    minWidth: 0,
    position: "relative",
    zIndex: 1,
    display: "flex",
    padding: "0",
    margin: "0",
    boxSizing: "border-box",
    overflow: "hidden",
    isolation: "isolate",
    color: "#ffffff",
    borderRadius: 28,
    border: "1px solid rgba(21, 41, 43, 0.06)",
    // No drop shadow. The illustration is a dark teal block on a white page, so
    // it already reads as a distinct panel; the halo the two screens carried
    // before this layout existed (Login 40px/0.22, Registration 24px/0.18) only
    // softened its edge. Dropping it also removes a subtle hazard: box-shadow
    // ink overflow counts toward the scrollable area of any ancestor with
    // overflow != visible, which had been tipping an otherwise
    // fits-in-one-viewport layout into a phantom scrollbar.
    boxShadow: "none",
    // Soft ambient glow anchored to the upper-right, breathing in opacity. Two
    // off-center radial highlights (white + mint) with no hard center seam.
    // Sits above brandImage (zIndex -2) but below normal content, so it reads
    // as a light hitting the illustration rather than the page behind it.
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      zIndex: -1,
      background: [
        "radial-gradient(48% 40% at 78% 12%, rgba(255,255,255,0.20), transparent 72%)",
        "radial-gradient(55% 45% at 92% 55%, rgba(120,240,220,0.16), transparent 70%)",
      ].join(","),
      animation: `${glow} 9s ease-in-out infinite`,
      pointerEvents: "none",
      "@media (prefers-reduced-motion: reduce)": {
        animation: "none",
      },
    },
  },
  brandImage: {
    position: "absolute",
    inset: 0,
    zIndex: -2,
    backgroundImage: `url(${LoginBrandBackground})`,
    backgroundSize: "cover",
    // >50% shifts the crop to show more of the image's right side, which
    // pushes the visible tree/icons left within the frame.
    backgroundPosition: "52% center",
    backgroundRepeat: "no-repeat",
    transform: "scale(1.1)",
  },

  // Wraps the logo as a link to the marketing site — pinned above the tree
  // (relies on brandPanel's own position:relative), horizontally centered.
  brandLogoLink: {
    position: "absolute",
    top: 18,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1,
    display: "flex",
    textDecoration: "none",
    transition: "transform 0.25s ease, filter 0.25s ease",
    "&:hover": {
      transform: "translateX(-50%) translateY(-2px)",
    },
    [`@media (max-width: ${theme.breakpoints.sm})`]: {
      top: 20,
    },
  },
  brandLogo: {
    // logo2.png is the white-on-transparent wordmark — needed for contrast
    // against the dark teal illustration behind it.
    width: 140,
    height: "auto",
    filter: "drop-shadow(0 4px 14px rgba(0,0,0,0.35))",
    [`@media (max-width: ${theme.breakpoints.sm})`]: {
      width: 112,
    },
  },
}));
